import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Check, Warning } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SubscriptionSuccessPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { checkAuth } = useAuth();
  const sessionId = searchParams.get('session_id');
  // Stripe Elements flow: passes `sub` (our id) and `payment_intent` + `redirect_status`
  const subId = searchParams.get('sub');
  const redirectStatus = searchParams.get('redirect_status'); // succeeded | processing | failed

  const [state, setState] = useState('verifying'); // verifying | success | pending | failed
  const [retrying, setRetrying] = useState(false);

  useEffect(() => {
    verify();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const verify = async () => {
    try {
      let res;
      // Backoff retry: webhook from Stripe can lag a few seconds → we poll
      // before giving up. Total worst-case ~17s.
      const backoffMs = [0, 1500, 2500, 4000, 6000];
      if (subId) {
        // New Elements flow
        if (redirectStatus === 'failed') {
          setState('failed');
          toast.error('Pagamento rifiutato. Riprova o usa un\'altra carta.');
          setTimeout(() => navigate(`/subscribe?plan=monthly`), 3500);
          return;
        }
        for (const delay of backoffMs) {
          if (delay) await new Promise((r) => setTimeout(r, delay));
          res = await axios.get(`${API}/subscription/verify-by-sub/${subId}`, { withCredentials: true });
          if (res.data.status === 'active') break;
        }
      } else if (sessionId) {
        // Hosted Checkout: retry too, the webhook can be slow
        for (const delay of backoffMs) {
          if (delay) await new Promise((r) => setTimeout(r, delay));
          res = await axios.get(`${API}/subscription/verify/${sessionId}`, { withCredentials: true });
          if (res.data.status === 'active') break;
        }
      } else {
        setState('failed');
        setTimeout(() => navigate('/subscription'), 2500);
        return;
      }

      if (res.data.status === 'active') {
        setState('success');
        toast.success('Abbonamento attivato!');
        await checkAuth();
        setTimeout(() => navigate('/dashboard'), 1800);
      } else {
        setState('pending');
        toast.info('Pagamento in elaborazione. Ti avviseremo via email quando sarà attivo.');
        // Do NOT auto-redirect: keep the user here so they can use the
        // "Verifica manualmente" button if the webhook never arrives.
      }
    } catch (err) {
      console.error('Verify error:', err);
      setState('failed');
      toast.error('Errore nella verifica. Usa il bottone qui sotto.');
    }
  };

  /** Last-resort recovery: ask backend to look up Stripe by user email.
   *  Used when Stripe takes longer than our retry budget. */
  const verifyByEmail = async () => {
    setRetrying(true);
    try {
      const res = await axios.post(
        `${API}/subscription/recover-by-current-user`,
        {},
        { withCredentials: true }
      );
      if (res.data.status === 'active') {
        setState('success');
        toast.success('Trovato! Abbonamento attivo.');
        await checkAuth();
        setTimeout(() => navigate('/dashboard'), 1500);
      } else {
        toast.error("Stripe non riporta ancora un abbonamento attivo. Riprova tra qualche minuto.");
      }
    } catch (err) {
      const detail = err?.response?.data?.detail || 'Errore. Contattaci a team@vitadahost.it';
      toast.error(typeof detail === 'string' ? detail : 'Stripe non ha trovato abbonamenti per la tua email.');
    }
    setRetrying(false);
  };

  return (
    <div className="min-h-screen bg-zinc-50 flex items-center justify-center p-6">
      <div className="max-w-md w-full bg-white border border-zinc-200 p-8 text-center" data-testid="subscription-success-page">
        {state === 'verifying' && (
          <>
            <div className="animate-spin w-12 h-12 border-4 border-zinc-200 border-t-zinc-900 rounded-full mx-auto mb-5" />
            <h1 className="text-xl font-black text-zinc-900 mb-2">Confermo il pagamento...</h1>
            <p className="text-sm text-zinc-500">Un attimo, sto sbloccando il tuo abbonamento.</p>
          </>
        )}
        {state === 'success' && (
          <>
            <div className="w-20 h-20 bg-emerald-500 flex items-center justify-center mx-auto mb-5">
              <Check size={40} weight="bold" className="text-white" />
            </div>
            <h1 className="text-2xl font-black text-zinc-900 mb-2">Abbonamento attivato</h1>
            <p className="text-sm text-zinc-500 mb-4">Ti sto portando alla dashboard...</p>
          </>
        )}
        {state === 'pending' && (
          <>
            <div className="animate-pulse w-20 h-20 bg-amber-500 flex items-center justify-center mx-auto mb-5">
              <Warning size={36} weight="bold" className="text-white" />
            </div>
            <h1 className="text-xl font-black text-zinc-900 mb-2">Pagamento in elaborazione</h1>
            <p className="text-sm text-zinc-500 mb-5 leading-relaxed">
              La banca sta confermando il pagamento. Se hai già ricevuto la conferma da Stripe via email
              e l'addebito sulla carta, clicca qui sotto per forzare la verifica.
            </p>
            <button
              onClick={verifyByEmail}
              disabled={retrying}
              className="w-full bg-zinc-900 text-white text-xs font-black uppercase tracking-wider py-3 hover:bg-zinc-800 disabled:opacity-50"
              data-testid="verify-by-email-btn"
            >
              {retrying ? 'Verifico...' : 'Ho già pagato → Verifica ora'}
            </button>
          </>
        )}
        {state === 'failed' && (
          <>
            <div className="w-20 h-20 bg-red-500 flex items-center justify-center mx-auto mb-5">
              <Warning size={36} weight="bold" className="text-white" />
            </div>
            <h1 className="text-xl font-black text-zinc-900 mb-2">Pagamento non verificato</h1>
            <p className="text-sm text-zinc-500 mb-5 leading-relaxed">
              Se hai già ricevuto la conferma da Stripe, clicca qui sotto per verificare.
              Altrimenti scrivici a <strong>team@vitadahost.it</strong>.
            </p>
            <button
              onClick={verifyByEmail}
              disabled={retrying}
              className="w-full bg-zinc-900 text-white text-xs font-black uppercase tracking-wider py-3 hover:bg-zinc-800 disabled:opacity-50"
              data-testid="verify-by-email-btn"
            >
              {retrying ? 'Verifico...' : 'Ho già pagato → Verifica ora'}
            </button>
          </>
        )}
      </div>
    </div>
  );
}
